import { ChevronDown } from 'lucide-react'
import { type ReactNode, useState } from 'react'
import './disclosure.css'

/**
 * A titled section that folds away. It exists so a long form can present its
 * shape first — the reader sees every section's name at once and opens the one
 * they came for — rather than presenting a wall of fields and asking them to
 * scroll for the structure.
 *
 * `note` is the one-line summary shown while closed: a folded section still has
 * to say what is inside it, or folding just hides state.
 */
export function Disclosure({
  title,
  note,
  defaultOpen = false,
  flush = false,
  children,
}: {
  title: string
  note?: ReactNode
  defaultOpen?: boolean
  /**
   * Drops the horizontal gutters, for a surface that already has its own — a
   * companion band, a card body. The default ones are a panel's, and inside
   * something that has already inset its contents they indent the fold away
   * from everything standing beside it.
   */
  flush?: boolean
  children: ReactNode
}) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <section
      className="disclosure"
      data-open={open ? '' : undefined}
      data-flush={flush ? '' : undefined}
    >
      <button
        type="button"
        className="disclosure__trigger"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="disclosure__title">{title}</span>
        {note && !open ? (
          <span className="disclosure__note">{note}</span>
        ) : null}
        <ChevronDown className="disclosure__chevron" size={16} aria-hidden />
      </button>
      {open ? <div className="disclosure__body">{children}</div> : null}
    </section>
  )
}
